import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useResolveException, useDismissException } from '../hooks/useAccDashboard';
import { formatRelativeAge, formatCad } from '../utils';
import type { ReviewQueueItem } from '../types';

interface ReviewQueueProps {
  items: ReviewQueueItem[] | null;
  degraded: boolean;
  loading: boolean;
}

/** Maps errorType strings to a visual variant. */
function errorTypeBadge(errorType: string) {
  const lower = errorType.toLowerCase();
  if (lower.includes('match') || lower.includes('ambigu')) {
    return <Badge variant="outline" className="border-amber-400 text-amber-700 whitespace-nowrap text-xs">{errorType}</Badge>;
  }
  if (lower.includes('fail') || lower.includes('error') || lower.includes('invalid')) {
    return <Badge variant="destructive" className="whitespace-nowrap text-xs">{errorType}</Badge>;
  }
  return <Badge variant="secondary" className="whitespace-nowrap text-xs">{errorType}</Badge>;
}

/** Age badge: red ≥30d, amber ≥7d, muted otherwise. */
function ageBadge(updatedAt: string, createdAt: string) {
  const label = formatRelativeAge(updatedAt, createdAt);

  // Parse hours for colouring
  const diffMs = Date.now() - new Date(updatedAt || createdAt).getTime();
  const hours = diffMs / 3_600_000;

  if (hours >= 24 * 30) {
    return <Badge variant="destructive" className="tabular-nums text-xs">{label}</Badge>;
  }
  if (hours >= 24 * 7) {
    return <Badge variant="outline" className="border-amber-400 text-amber-700 tabular-nums text-xs">{label}</Badge>;
  }
  return <Badge variant="secondary" className="tabular-nums text-xs">{label}</Badge>;
}

function RowActions({ item }: { item: ReviewQueueItem }) {
  const resolve = useResolveException();
  const dismiss = useDismissException();
  const [dismissOpen, setDismissOpen] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="default"
        disabled={resolve.isPending || dismiss.isPending}
        onClick={() => resolve.mutate({ id: item.id })}
        className="h-7 text-xs"
      >
        Resolve
      </Button>

      <AlertDialog open={dismissOpen} onOpenChange={setDismissOpen}>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            disabled={resolve.isPending || dismiss.isPending}
            className="h-7 text-xs"
          >
            Dismiss
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss exception?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark exception <span className="font-mono">{item.id.slice(0, 8)}</span> as
              dismissed. The action is logged in the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                dismiss.mutate({ id: item.id });
                setDismissOpen(false);
              }}
            >
              Dismiss
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function ReviewQueue({ items, degraded, loading }: ReviewQueueProps) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full rounded" />
        ))}
      </div>
    );
  }

  if (degraded) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground border border-dashed rounded-lg">
        Review queue unavailable — backend section degraded.
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground border border-dashed rounded-lg">
        No open exceptions — all clear.
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-36">Issue</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="w-28 text-right">Amount</TableHead>
              <TableHead className="w-40">Entity</TableHead>
              <TableHead className="w-20">Age</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-36 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                {/* Issue — errorType badge */}
                <TableCell className="align-top pt-3">
                  {errorTypeBadge(item.errorType)}
                </TableCell>

                {/* Message + suggested action */}
                <TableCell className="align-top pt-3 max-w-sm">
                  <p
                    className="text-sm leading-snug line-clamp-2"
                    title={item.errorMessage}
                  >
                    {item.errorMessage}
                  </p>
                  {item.suggestedAction && (
                    <span className="mt-1 inline-block text-xs text-muted-foreground italic">
                      {item.suggestedAction}
                    </span>
                  )}
                </TableCell>

                {/* Amount */}
                <TableCell className="align-top pt-3 text-right tabular-nums whitespace-nowrap">
                  {item.amount != null ? (
                    <strong className="text-sm">{formatCad(item.amount)}</strong>
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </TableCell>

                {/* Entity — label, not raw id */}
                <TableCell className="align-top pt-3 text-xs text-muted-foreground">
                  <span className="font-mono uppercase">{item.entityType}</span>
                  <br />
                  <span className="text-foreground/80 normal-case">{item.entityLabel || item.entityId.slice(0, 8)}</span>
                </TableCell>

                {/* Age — relative time from updatedAt */}
                <TableCell className="align-top pt-3">
                  {ageBadge(item.updatedAt, item.createdAt)}
                </TableCell>

                {/* Status */}
                <TableCell className="align-top pt-3">
                  <Badge variant="outline" className="text-xs capitalize">
                    {item.status}
                  </Badge>
                </TableCell>

                {/* Actions */}
                <TableCell className="align-top pt-3 text-right">
                  <RowActions item={item} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
